'use client';

import React from 'react';

type Parts = [string, string, string];

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function clockParts(): Parts {
  const d = new Date();
  return [pad(d.getHours()), pad(d.getMinutes()), pad(d.getSeconds())];
}

function elapsedParts(since: number): Parts {
  const total = Math.max(0, Math.floor((Date.now() - since) / 1000));
  return [pad(Math.floor(total / 3600)), pad(Math.floor((total % 3600) / 60)), pad(total % 60)];
}

/**
 * HH:MM:SS in .m-mono with the colons blinking once per second (CSS). Either a
 * time-of-day clock (default) or an elapsed timer counted from `since` (epoch
 * ms). Ticks on the second boundary; renders dashes until mounted so SSR and
 * hydration agree.
 */
export function Timecode({
  mode = 'clock',
  since,
  className,
}: {
  mode?: 'clock' | 'elapsed';
  since?: number;
  className?: string;
}) {
  const [parts, setParts] = React.useState<Parts | null>(null);
  const ref = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    const tick = () => {
      setParts(mode === 'elapsed' ? elapsedParts(since ?? Date.now()) : clockParts());
    };
    tick();

    // Start the CSS blink in phase with the wall clock so the colon and the
    // digits change together.
    const el = ref.current;
    if (el) {
      const offset = -(Date.now() % 1000);
      el.querySelectorAll<HTMLElement>('.m-tc-colon').forEach((c) => {
        c.style.animationDelay = `${offset}ms`;
      });
    }

    let interval: number | undefined;
    const timeout = window.setTimeout(
      () => {
        tick();
        interval = window.setInterval(tick, 1000);
      },
      1000 - (Date.now() % 1000),
    );
    return () => {
      window.clearTimeout(timeout);
      if (interval !== undefined) window.clearInterval(interval);
    };
  }, [mode, since]);

  const [h, m, s] = parts ?? ['--', '--', '--'];
  const cls = className ? `m-mono m-timecode ${className}` : 'm-mono m-timecode';
  const label = parts
    ? mode === 'elapsed'
      ? `Elapsed ${h}:${m}:${s}`
      : `${h}:${m}:${s}`
    : undefined;

  return (
    <span ref={ref} className={cls} aria-label={label} role={mode === 'elapsed' ? 'timer' : undefined}>
      {h}
      <span className="m-tc-colon" aria-hidden="true">
        :
      </span>
      {m}
      <span className="m-tc-colon" aria-hidden="true">
        :
      </span>
      {s}
    </span>
  );
}
